import { LitElement, html } from 'lit';

import { api } from '../../api/index.js';
import { confirmAction } from '../../components/plane-confirm.js';
import { customerStateBadge } from '../../components/plane-badge.js';
import { customerLoadState } from '../../components/customer-load-state.js';
import { markdown } from '../../components/plane-markdown.js';
import { when } from '../../components/time.js';
import { CUSTOMER_PERSONA, assistantName, assistantPath, assistantTelegramSecret, customerError, customerStateOf, onlyAssistant, ownersOnly, WORKSPACE_NAME_MAX, workspaceName, workspacePath } from '../../i18n/customer-labels.js';
import { poll } from '../../poll.js';
import { delegatesOf, orchestratorOf, teamOrder } from '../../team.js';
import { customerStyles } from '../../../styles/shared/customer.js';
import { formStyles } from '../../../styles/shared/forms.js';
import { markdownStyles } from '../../../styles/shared/markdown.js';
import { pageStyles } from '../../../styles/shared/page.js';
import { customerWorkspacePageStyles } from '../../../styles/pages/customer/workspace-page.js';

/**
 * One workspace (an instance), as its customer sees it: how it is doing in a sentence, the
 * assistant that lives in it with its personality, its Telegram, and the history of the changes
 * with a way back to an earlier version. A workspace with one assistant shows it as the
 * workspace's own, with its first steps; one with several shows a card for each, with its own Edit
 * and Telegram. State, restart and history stay the workspace's.
 *
 * Saving here is always "save and apply": a customer has no drafts. A version is a published
 * revision; going back to one is a rollback (InstanceCommands.makeDesired). Only the owners of the
 * customer change it (`canManage`): a member reads it and may restart it.
 */
class WorkspacePage extends LitElement {
  static properties = {
    name: { type: String },
    busy: { state: true },
    actionError: { state: true },
    renaming: { state: true },
  };

  static styles = [
    pageStyles,
    formStyles,
    customerStyles,
    markdownStyles,
    customerWorkspacePageStyles,
  ];

  constructor() {
    super();
    this.busy = false;
    this.actionError = null;
    this.renaming = false;
    this.poll = poll(this, async () => {
      const instance = await api.instance(this.name);
      const desired = instance.desiredRevision ? await api.revision(this.name, instance.desiredRevision.id) : null;
      return { instance, desired };
    });
  }

  updated(changed) {
    if (changed.has('name') && changed.get('name') !== undefined) {
      this.actionError = null;
      this.renaming = false;
      this.poll.reset();
    }
    // reached by the name of the instance (a link of the operator's): the address takes the ref a
    // customer knows it by, so that the name of the gateway does not stay in sight
    const ref = this.poll.value?.instance.ref;
    if (ref && ref !== this.name) {
      window.history.replaceState({}, '', workspacePath(ref));
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }

  /**
   * Runs an action, then reloads: what the page shows next is what the workspace reports.
   *
   * @param explain what to tell the customer when it fails
   */
  async act(action, explain = customerError) {
    this.busy = true;
    this.actionError = null;
    try {
      await action();
    } catch (error) {
      this.actionError = explain(error);
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  async restart(workspace) {
    if (!(await confirmAction({ heading: `Restart the workspace ${workspace}?`, message: 'Its assistant does not answer for a few seconds.', confirmLabel: 'Restart', tone: 'danger' }))) {
      return;
    }
    this.act(() => api.restart(this.name));
  }

  /** Renames the workspace; the form stays open when the name is refused. */
  async rename(event) {
    event.preventDefault();
    const name = event.target.elements['workspace-name'].value;
    this.busy = true;
    this.actionError = null;
    try {
      await api.renameWorkspace(this.name, name);
      this.renaming = false;
    } catch (error) {
      this.actionError = error.status === 400 ? `Give it a name of 1 to ${WORKSPACE_NAME_MAX} characters.` : customerError(error);
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  renderRename(instance, workspace) {
    return html`
      <form class="card" @submit=${(event) => this.rename(event)}>
        <div class="field">
          <label for="workspace-name">Name of the workspace</label>
          <span class="help">How you and the other people of ${instance.customer} call it. Up to ${WORKSPACE_NAME_MAX} characters.</span>
          <input id="workspace-name" name="workspace-name" type="text" required maxlength=${WORKSPACE_NAME_MAX} ?disabled=${this.busy} .value=${workspace} />
        </div>
        <div class="buttons">
          <button class="primary" type="submit" ?disabled=${this.busy}>Save</button>
          <button type="button" ?disabled=${this.busy} @click=${() => (this.renaming = false)}>Cancel</button>
        </div>
      </form>
    `;
  }

  /** Lets the sender of a pairing request write to the assistant: a new version with them among the allowed. */
  async allow(id, request) {
    const who = senderLabel(request);
    if (
      !(await confirmAction({
        heading: `Let ${who} write to the assistant?`,
        message: `Make sure it is them: ask ${who} for the code the bot sent, it must be ${request.code}. You can remove them later in Edit.`,
        confirmLabel: 'Allow',
      }))
    ) {
      return;
    }
    this.act(() => api.allowPairingRequest(this.name, id, channelOf(request), request.senderId));
  }

  ignore(id, request) {
    this.act(() => api.ignorePairingRequest(this.name, id, channelOf(request), request.senderId));
  }

  async goBack(revision) {
    if (
      !(await confirmAction({
        heading: `Go back to the version of ${new Date(revision.createdAt).toLocaleString()}?`,
        message: 'Your later changes stay in the history.',
        confirmLabel: 'Go back',
      }))
    ) {
      return;
    }
    this.act(
      () => api.apply(this.name, revision.id),
      // a version from before the operator changed the model: it cannot come back
      (error) => (error.status === 403 ? 'This version used settings that are no longer available: it cannot come back.' : customerError(error)),
    );
  }

  /**
   * What is left to do before the assistant is the customer's own and answers people: the steps
   * still open, read from what the workspace reports. Revision 1 is the operator's (a template, or
   * their first configuration): the customer has not made it theirs yet. Owners only.
   */
  renderSteps(instance, desired, id, name) {
    if (!instance.canManage || !desired) {
      return null;
    }
    const base = workspacePath(this.name);
    const secret = instance.secrets.find((candidate) => candidate.name === assistantTelegramSecret(id));
    const steps = [
      desired.number === 1 && html`<li><a href="${base}/edit">Make ${name} yours</a>: its name, its picture, and what it should know about you.</li>`,
      secret && !secret.set && html`<li><a href="${base}/telegram">Connect Telegram</a>, so that people can write to it.</li>`,
      secret && answersNobody(desired.params.assistants[id]) && html`<li><a href="${base}/edit">Choose who can write to it</a>: nobody gets an answer until you do.</li>`,
      waitingFor(instance, id).length > 0 && html`<li><a href="#telegram">Allow the people waiting</a> to write to ${name}.</li>`,
    ].filter(Boolean);
    if (steps.length === 0) {
      return null;
    }
    return html`
      <section class="card">
        <h2>Getting started</h2>
        <ol class="steps">
          ${steps}
        </ol>
      </section>
    `;
  }

  /**
   * The Telegram bot of an assistant: whether it is connected, who can write to it.
   *
   * @param params the params of the assistant
   * @param secretName its bot token secret
   * @param base where its pages are: base/telegram connects the bot, base/edit edits it
   * @param id the id of the assistant, for its pairing requests
   * @param heading the h2 of the card, or null for an h3 inside the card of the assistant
   */
  renderTelegram(instance, params, id, base, heading = 'Telegram') {
    const secretName = assistantTelegramSecret(id);
    const secret = instance.secrets.find((candidate) => candidate.name === secretName);
    if (!secret) {
      return null;
    }
    const telegram = params?.telegram ?? {};
    const allowed = telegram.allowFrom ?? [];
    const connect = instance.canManage
      ? html`<a class="button ${secret.set ? '' : 'primary'}" href="${base}/telegram"
          >${secret.set ? 'Change bot' : 'Connect Telegram'}</a
        >`
      : null;
    let status;
    if (!secret.set) {
      status = html`<p>Not connected yet: nobody can talk to your assistant until a Telegram bot is connected.</p>`;
    } else if (secret.rejection) {
      status = html`<p>The bot could not be connected: it must be connected again.</p>`;
    } else if (!secret.deliveredAt) {
      status = html`<p>Connecting… it takes a minute.</p>`;
    } else if (channelState(instance, id, 'telegram')?.state === 'disconnected') {
      const { error } = channelState(instance, id, 'telegram');
      status = html`<div class="notice" data-tone="warning" role="status">
        <strong>The bot is not running</strong>Nobody gets an answer until it runs again.${error ? html` Telegram says: <code>${error}</code>` : null}
        ${instance.canManage ? html`If you changed the bot in BotFather, <a href="${base}/telegram">connect it again</a>.` : ''}
      </div>`;
    } else {
      status = html`<p>Connected ${when(secret.updatedAt)}.</p>`;
    }
    // a connected bot that nobody may write to looks broken: it never answers
    const nobody =
      secret.set && answersNobody(params)
        ? html`<div class="notice" data-tone="warning" role="status">
            <strong>Nobody gets an answer yet</strong>The bot is connected, but it ignores every message until you list who can write to it, by Telegram user id, or let
            people ask.
            ${instance.canManage ? html`<a href="${base}/edit">Choose who can write to it</a>` : 'Ask an owner of the workspace to add you.'}
          </div>`
        : null;
    const open = telegram.public
      ? html`<div class="notice" role="status">
          <strong>Open to everyone</strong>Anyone who finds the bot on Telegram can talk to it. The people listed below are the only ones who can use its commands.
        </div>`
      : null;
    let newcomers;
    if (telegram.public) {
      newcomers = 'Anyone can write to it';
    } else if (telegram.acceptRequests) {
      newcomers = 'Can ask: the bot sends them a code, and they wait until an owner allows them here';
    } else {
      newcomers = html`Cannot ask${instance.canManage ? html`: <a href="${base}/edit">let people ask</a>, so that you do not need their user id` : ''}`;
    }
    const content = html`
      ${status} ${nobody} ${open}
      ${this.renderRequests(instance, id, telegram)}
      <dl class="facts">
        <dt>Who can write to it</dt>
        <dd>${allowed.length > 0 ? allowed.join(', ') : html`<span class="muted">Nobody yet${instance.canManage && !nobody ? ': add people in Edit' : ''}</span>`}</dd>
        <dt>Somebody new</dt>
        <dd>${newcomers}</dd>
        ${'groups' in telegram ? html`<dt>Group chats</dt><dd>${telegram.groups ? 'Answers when mentioned' : 'Does not answer'}</dd>` : null}
      </dl>
    `;
    if (heading === null) {
      return html`<div class="persona" id="telegram-${id}"><h3>Telegram ${connect}</h3>${content}</div>`;
    }
    return html`
      <section class="card" id="telegram">
        <h2>${heading} ${connect}</h2>
        ${content}
      </section>
    `;
  }

  /**
   * Who wrote to the bot and waits (pairing): owners allow or ignore them. A request lasts an hour
   * in the bot; the code is what the person got, to make sure it is them.
   */
  renderRequests(instance, id, telegram) {
    const waiting = waitingFor(instance, id, 'telegram');
    if (waiting.length === 0) {
      return telegram.acceptRequests && !telegram.public && instance.canManage
        ? html`<p class="muted">Nobody is waiting. Whoever writes to the bot gets a code, and shows up here for an hour.</p>`
        : null;
    }
    return html`
      <div class="notice" data-tone="warning" role="status">
        <strong>${waiting.length === 1 ? 'Somebody wants' : `${waiting.length} people want`} to talk to the assistant</strong>
        Allow only people you know: ask them for the code the bot sent them.
        <ul class="requests">
          ${waiting.map(
            (request) => html`
              <li>
                <span>${senderLabel(request)} · code <code>${request.code}</code> · ${when(request.requestedAt)}</span>
                <span class="buttons">
                  <button class="small primary" ?disabled=${this.busy} @click=${() => this.allow(id, request)}>Allow</button>
                  <button class="small" ?disabled=${this.busy} @click=${() => this.ignore(id, request)}>Ignore</button>
                </span>
              </li>
            `,
          )}
        </ul>
      </div>
    `;
  }

  renderPersona(persona) {
    return CUSTOMER_PERSONA.map(
      (part) => html`
        <div class="persona">
          <h3>${part.title}</h3>
          ${persona?.[part.key]?.trim() ? html`<div class="markdown">${markdown(persona[part.key])}</div>` : html`<p class="muted">Not written yet.</p>`}
        </div>
      `,
    );
  }

  renderOperatorRules(desired) {
    return desired?.operatorRules?.trim()
      ? html`
          <div class="persona">
            <h3>Rules from your provider</h3>
            <div class="markdown">${markdown(desired.operatorRules)}</div>
          </div>
        `
      : null;
  }

  /** Where an assistant stands in the team of the workspace (plane/docs/team-proposal.md); nothing outside a team. */
  renderRole(params, id) {
    const nameOf = (other) => params.assistants[other]?.agentName ?? other;
    const delegates = delegatesOf(params, id);
    if (delegates.length > 0) {
      return html`<p class="muted">People talk to ${nameOf(id)}, and ${nameOf(id)} asks ${delegates.map((delegate, i) => html`${i > 0 ? (i === delegates.length - 1 ? ' and ' : ', ') : ''}<a href="#assistant-${delegate}">${nameOf(delegate)}</a>`)} when a question needs them.</p>`;
    }
    const orchestrator = orchestratorOf(params, id);
    if (!orchestrator) {
      return null;
    }
    return html`
      <p class="muted">In the team of <a href="#assistant-${orchestrator}">${nameOf(orchestrator)}</a>: people do not write to it, ${nameOf(orchestrator)} asks it when a question needs it.</p>
      <div class="persona">
        <h3>What it is for</h3>
        ${params.assistants[id]?.description?.trim() ? html`<p class="excerpt">${params.assistants[id].description}</p>` : html`<p class="muted">Not written yet.</p>`}
      </div>
    `;
  }

  /** The assistants of a workspace with several: a card each, with its persona and its bot, in the order of their team. */
  renderAssistants(instance, desired) {
    const ids = teamOrder(desired.params).filter((id) => id in desired.personas);
    return html`
      ${desired.operatorRules?.trim()
        ? html`<section class="card"><h2>Rules from your provider</h2><p class="muted">Every assistant follows them, before your own rules.</p><div class="markdown">${markdown(desired.operatorRules)}</div></section>`
        : null}
      ${ids.map((id) => {
        const params = desired.params.assistants[id] ?? {};
        const base = assistantPath(this.name, id);
        return html`
          <section class="card" id="assistant-${id}">
            <h2>Assistant ${instance.canManage ? html`<a class="button" href="${base}/edit">Edit</a>` : null}</h2>
            <div class="identity">
              ${params.avatar ? html`<img class="avatar" src=${params.avatar} alt="" />` : null}
              <h3>${params.agentName ?? id}</h3>
            </div>
            ${this.renderRole(desired.params, id)} ${this.renderPersona(desired.personas[id])} ${this.renderTelegram(instance, params, id, base, null)}
          </section>
        `;
      })}
    `;
  }

  /** The one assistant of a workspace, as the workspace's own; id null before it has a revision. */
  renderAssistant(instance, name, desired, id) {
    const params = desired?.params.assistants[id];
    return html`
      <section class="card">
        <h2>Assistant ${instance.canManage && desired ? html`<a class="button" href="${workspacePath(this.name)}/edit">Edit</a>` : null}</h2>
        <div class="identity">
          ${params?.avatar ? html`<img class="avatar" src=${params.avatar} alt="" />` : null}
          <h3>${name}</h3>
        </div>
        ${this.renderOperatorRules(desired)}
        ${desired ? this.renderPersona(desired.personas[id]) : html`<p class="muted">Your assistant is being set up.</p>`}
      </section>
    `;
  }

  renderHistory(instance) {
    // drafts are an operator's tool: a customer saves and applies at once
    const versions = instance.revisions.filter((revision) => revision.status === 'published');
    if (versions.length === 0) {
      return null;
    }
    return html`
      <section class="card">
        <h2>History</h2>
        <ul class="history">
          ${versions.map((revision) => {
            const current = revision.id === instance.desiredRevision?.id;
            return html`
              <li>
                <span class=${current ? 'current' : ''}>
                  ${when(revision.createdAt)} · by ${revision.author}${current ? ' · current version' : ''}
                </span>
                ${current || !instance.canManage
                  ? null
                  : html`<button class="small" ?disabled=${this.busy} @click=${() => this.goBack(revision)}>Go back to this version</button>`}
              </li>
            `;
          })}
        </ul>
      </section>
    `;
  }

  render() {
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/">Your workspaces</a></nav>`;
    const pending = customerLoadState(this.poll);
    if (pending) {
      return html`${crumbs}${pending}`;
    }
    const { instance, desired } = this.poll.value;
    const name = assistantName(instance);
    const only = onlyAssistant(desired);
    const workspace = workspaceName(instance);
    const state = customerStateOf(instance.state);
    return html`
      ${crumbs}
      <header class="page-head">
        <div>
          <h1>${workspace} ${customerStateBadge(instance.state)}</h1>
          <p class="lead">${state.text(workspace)}</p>
        </div>
        <div class="actions">
          ${instance.canManage && !this.renaming ? html`<button ?disabled=${this.busy} @click=${() => (this.renaming = true)}>Rename</button>` : null}
          <button ?disabled=${this.busy || !desired} @click=${() => this.restart(workspace)}>Restart</button>
        </div>
      </header>
      ${this.renaming ? this.renderRename(instance, workspace) : null}
      ${instance.canManage ? null : html`<p class="muted">${ownersOnly(instance)}</p>`}
      ${this.actionError
        ? html`<div class="notice" data-tone="danger" role="alert">
            <strong>That did not work</strong>${this.actionError}
          </div>`
        : null}
      ${desired && !only
        ? this.renderAssistants(instance, desired)
        : html`${this.renderSteps(instance, desired, only, name)}
          ${only ? this.renderTelegram(instance, desired.params.assistants[only], only, workspacePath(this.name)) : null}
          ${this.renderAssistant(instance, name, desired, only)}`}
      ${this.renderHistory(instance)}
    `;
  }
}

customElements.define('plane-workspace-page', WorkspacePage);

/** Whether the bot of an assistant answers nobody: nobody allowed, nobody can ask, not open. */
function answersNobody(params) {
  const telegram = params?.telegram ?? {};
  return (telegram.allowFrom ?? []).length === 0 && !telegram.acceptRequests && !telegram.public;
}

/** The pairing requests waiting for an assistant on a channel; none for a member, who does not see them. */
function waitingFor(instance, id, channel) {
  return (instance.pairingRequests ?? []).filter((request) => request.assistant === id && channelOf(request) === channel);
}

/** The channel of a pairing request: Telegram for a plane that does not name it. */
function channelOf(request) {
  return request.channel ?? 'telegram';
}

/** The state of the account of an assistant on a channel; null when it is not known. */
function channelState(instance, id, channel) {
  return (instance.channels ?? []).find((candidate) => candidate.assistant === id && candidate.channel === channel) ?? null;
}

/** Who sent a pairing request, as the channel names them. */
function senderLabel(request) {
  const name = request.name ?? null;
  const username = request.username ? `@${request.username}` : null;
  if (name && username) {
    return `${name} (${username})`;
  }
  return name ?? username ?? (channelOf(request) === 'whatsapp' ? request.senderId : `Telegram user ${request.senderId}`);
}
